import { HandleTask } from "../Components/Models/HandleTask/HandleTask";
import { coreupdate } from "../Constants/Actions";
import { loadData, UserDataProvider } from "../Core/UserDataProvider";
import { authBody, tokenBody } from "../DataTypes/authResponse";
import { langs } from "../DataTypes/languageTypes";
import { UPDATE } from "../DataTypes/UPDATE";
import { DateParse, dayDifferenceFromNow } from "../Helpers/DateParse";
import { stringParser } from "../Helpers/Helpers";
import { readData } from "../Helpers/readData";
import { saveData } from "../Helpers/saveData";
import { controllers } from "./Controllers";
import { navigator } from "./Navigator";

// Read/written synchronously (unlike the rest of this controller's
// saveMe/restoreMe cycle, which is async and only resolves after mount) so
// the drawer's collapsed/expanded width is correct on its very first render
// instead of flashing from default to restored right after mount.
const DRAWER_STORAGE_KEY = 'wyens_drawer_collapsed'

export type signInResponseType = {
    status: boolean;
    message: string;
}
type entireInfoType = {
    user: userInfoType,
    seller: sellerInfoType
}
export type sellerInfoType = {
    id:string,
    display_name:string,
    given_name:string,
    family_name:string,
    description:string,
    account_type:string,
    skills:Array<any>,
    website:string,
    project_count:string,
    company_name:string,
    company_description:string,
    team_size:string,
    services_offered:any,
    years_in_business:string,
    created_at:string,
    updated_at:string,
    rating?: any;
    reviews?: any;
}
export type userInfoType = {
    id?:any;
    email:string;
    first_name:string;
    last_name:string;
    user_memory:string;
    username: string;
    is_google_auth:boolean;
    image_url: string;
    is_email_verified: boolean;
    profile_picture: string;
}

class AuthController {
    private _isLogin:boolean;
    private _userToken: string | null
    private _tokenForChat: string | null = null;
    private _userIdForChat: any = null;
    private _sellerInfo: sellerInfoType| null
    private _userInfo: userInfoType | null;
    private _remindMe: any;
    private _authHandlerTasks: HandleTask;
    private _lastLiveChannel: string | null;
    private _triedToResporeData: boolean;
    private _resoreDataHandler: HandleTask;
    private _afterAuthMethod: string = "";
    private _drawerCollapsed: boolean;
    private _canManageLeads: boolean = false;
    private _isAdmin: boolean = false;

    constructor(){
        this._isLogin = false
        this._userToken = null
        this._userInfo = null
        this._sellerInfo = null
        this._triedToResporeData = false
        this._drawerCollapsed = this.readStoredDrawerCollapsed()
        this._resoreDataHandler = new HandleTask();
        this._authHandlerTasks = new HandleTask();
        this._lastLiveChannel = null
        this.checkToken = this.checkToken.bind(this)
        this.signOut = this.signOut.bind(this)
        this.signIn = this.signIn.bind(this)
        this.getUserInfo = this.getUserInfo.bind(this)
        this.setRememberMe = this.setRememberMe.bind(this)
    }

    get restoreDataHandler(){
        return this._resoreDataHandler
    }

    get triedToResporeData(){
        return this._triedToResporeData
    }

    setTriedToRestoreData = (bool: boolean) => {
        this._triedToResporeData = bool;
    }

    get tasks(){
        return this._authHandlerTasks
    }

    setAfterAuth = (action: string) => {
        this._afterAuthMethod = action
    }

    setRememberMe(ref: any){
        this._remindMe = ref
    }

    get rememberMe(){
        try{
            return this._remindMe.state.checked
        } catch(e){
            return false
        }
    }

    get userInfo(){
        return this._userInfo
    }

    get sellerInfo(){
        return this._sellerInfo
    }

    get isLogin(){
        return this._isLogin
    }
    
    get userToken(){
        return this._userToken
    }

    get tokenForChat(){
        return this._userToken || this._tokenForChat
    }
    
    get userIdForChat(){
        return this._userInfo?.id || this._userIdForChat
    }

    get lastLiveChannel(){
        return this._lastLiveChannel
    }

    setLastLiveChannel(val:string = ''){
        this._lastLiveChannel = val
        this.saveMe()
    }

    get drawerCollapsed(){
        return this._drawerCollapsed
    }

    // Whether this user can manage leads at all, and whether they're a
    // lead-admin (sees/edits everyone's data, can publish shared templates
    // and edit the shared signature). Fetched once at login/restore — see
    // checkLeadAccess() — so admin-gated UI knows from the start instead of
    // showing admin controls to everyone by default.
    get canManageLeads(){
        return this._canManageLeads
    }

    get isAdmin(){
        return this._isAdmin
    }

    checkLeadAccess = async () => {
        const tokenBody: tokenBody = {
            userToken: this._userToken || ""
        }
        const response = await loadData(UserDataProvider.checkLeadAccess, tokenBody)
        if(response.statusCode === 200 && response.data){
            this._canManageLeads = !!response.data.can_manage_leads
            this._isAdmin = !!response.data.is_admin
            this.updateMe()
        }
    }

    setDrawerCollapsed = (collapsed: boolean) => {
        this._drawerCollapsed = collapsed
        try {
            window.localStorage.setItem(DRAWER_STORAGE_KEY, String(collapsed))
        } catch(e) {}
    }

    private readStoredDrawerCollapsed(): boolean {
        try {
            return window.localStorage.getItem(DRAWER_STORAGE_KEY) === 'true'
        } catch(e) {
            return false
        }
    }

    get all(){
        return {
            isLogin: this._isLogin,
            userToken: this.userToken,
            userInfo: this._userInfo,
            sellerInfo: this._sellerInfo,
            lastLiveChannel: this._lastLiveChannel,
            language: this.language,
            tokenForChat: this._tokenForChat,
            userIdForChat: this._userIdForChat
        }
    }

    // sign in btn press action
    async signIn(authBody:any){
        // const authBody: authBody = {
        //     email: login,
        //     password: password,
        //     remember_me: this.rememberMe
        // }
        // console.log('authBody', authBody)
        const response = await loadData(UserDataProvider.authorization,authBody);
        // console.log('response', response)
        if(response.statusCode !== 200 || !response?.data?.userToken){
            return response
        }
        this.login(response?.data?.userToken)
        return response
    }

    async signUp(authBody: any){
        // console.log('authBody', authBody)
        const response = await loadData(UserDataProvider.register,authBody);
        // console.log('response', response)
        if(response.statusCode !== 200 || !response?.data?.userToken){
            // controllers().alert.error(response.statusMessage)
            controllers().gTag.registerInited()
            return response
            return {
                status: false,
                message: response.statusMessage && response.statusMessage!==""? response.statusMessage : "Something went wrong"
            }
        }
        this.login(response?.data?.userToken)
        return response
        return {
            status: true,
            // message: response.statusMessage
        }
    }
    // do private auth with token
    login(userToken:string){
        if(!userToken){
            return false
        }
        this._isLogin = true
        this._userToken = userToken;
        this.getUserInfo()
        this.checkLeadAccess()
        this.saveMe()
        this.updateMe()
        this.afterAuth()
        // navigator().navigate('dashboard')
    }

    loginForChat(userToken:string, userId: any = null){
        if(!userToken){
            return false
        }
        this._isLogin = true
        this._tokenForChat = userToken;
        if(userId){
            this._userIdForChat = userId
        }
        // this._userToken = userToken;
        // this.getUserInfo()
        this.saveMe()
        this.updateMe()
        this.afterAuth()
        // this.saveMe()
        // this.updateMe()
        // this.afterAuth()
        // navigator().navigate('dashboard')
    }

    afterAuth = () => {

    }

    async signOut(){

        const tokenBody: tokenBody = {
            userToken: this._userToken||""
        }
        // sync request to server
        loadData(UserDataProvider.logout, tokenBody);
        // console.log('tokenBody response', response)
        // clear storage update page
        this.logout()

    }

    private logout(){
        this._isLogin = false
        this._userToken = null;
        this._userInfo = null;
        this._sellerInfo = null;
        this._canManageLeads = false;
        this._isAdmin = false;
        this.saveMe()
        this.updateMe()
        this.updateUser()
        // navigator().navigate("/")
    }

    async checkToken(){
        if(this._userToken===null){
            return 
        }
        // const tokenBody: tokenBody = {
        //     userToken: this._userToken||""
        // }
        // // sync request to server
        // const response = await loadData(UserDataProvider.check, tokenBody);
        // // wait success token
        // if(response.statusCode===200){
        //     return true
        // }
        // console.warn("CHECK TOKEN")
        // if(this._isLogin){
        //     this.logout()
        // }
        // if not 200 logout
        // return false
    }

    getUserInfo = async () => {
        const tokenBody: tokenBody = {
            userToken: this._userToken||""
        }
        // sync request to server
        const response = await loadData(UserDataProvider.userInfo, tokenBody);
        // wait success token
        if(response.statusCode===200){
            const data:entireInfoType = response.data
            this._userInfo = data.user
            this._sellerInfo = data.seller
            this._authHandlerTasks.do()
            this.updateUser()
        }

        // console.log("USER INFO", this._userInfo)
        // console.log("USER INFO", response.error)
        // if not 200 logout
        if(response.error){
            this.logout()
        }
    }

    private saveMe = async () => {
        const userData = JSON.stringify(this.all);
        await saveData('secureUserData', userData)
        return true
    }

    restoreMe = async () => {
        const data = await this.readData()
        if(!data){
            this.setTriedToRestoreData(true)
            this.updateUser();
            return
        }
        try{
            const userinfo = JSON.parse(data)
            // console.log('restoreMe userinfo',userinfo)
            this._isLogin = userinfo?.isLogin
            this._userToken = userinfo?.userToken
            this._userInfo = userinfo?.userInfo
            this._sellerInfo = userinfo?.sellerInfo
            this._tokenForChat = userinfo?.tokenForChat
            this._userIdForChat = userinfo?.userIdForChat
            controllers().language.setLanguage(userinfo?.language || "en")
            this.setTriedToRestoreData(true)
            if(this._isLogin){
                controllers().auth.getUserInfo()
                controllers().auth.checkLeadAccess()
                this.updateMe();
            }
            this.updateUser();
            this._resoreDataHandler.do()
        } catch(e){
            console.log('error restore data')
        }
    }

    get language(){
        return controllers().language.selectedLanguage
    }

    saveLanguage = () => {
        this.saveMe()
    }
    
    private async readData(): Promise<any> {
        const data = await readData('secureUserData');
        return data
    }

    updatePhoto = (photoPath: any) => {
        if(this.userInfo === null){
            return
        }
        this.userInfo.image_url = photoPath
        this.updateUser()
    }

    private updateMe(){
        coreupdate(UPDATE.AUTH)
    }

    private updateUser(){
        coreupdate(UPDATE.USERINFO)
    }

}

export { AuthController }